import type {
  ApolloError,
  FetchResult,
  InternalRefetchQueriesInclude,
} from '@apollo/client';
import type { GraphQLError } from 'graphql';

import {
  AdminListAppVersionSummariesDocument,
  AdminListAppVersionsDocument,
  useAdminCreateAppVersionDraft,
  useAdminListAppVersionSummaries,
  useAdminListAppVersions,
  useAdminPublishAppVersion,
  useAdminUpdateAppVersionDraft,
} from 'generated/app-version.generated';
import type {
  CreateAppVersionDraftInput,
  ListAppVersionsInput,
  PublishAppVersionInput,
  UpdateAppVersionDraftInput,
} from 'generated/types';

const summariesRefetch: InternalRefetchQueriesInclude = [
  { query: AdminListAppVersionSummariesDocument },
];

type MutationState = {
  loading: boolean;
  error?: ApolloError;
};

export const useAppVersionSummaries = () =>
  useAdminListAppVersionSummaries({
    errorPolicy: 'all',
  });

export const useAppVersionHistory = (
  input: ListAppVersionsInput,
  skip = false
) =>
  useAdminListAppVersions({
    variables: { input },
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

function toFetchErrorResult(error: ApolloError): FetchResult<unknown> {
  return {
    errors: (error.graphQLErrors.length
      ? error.graphQLErrors
      : [
          { message: error.message } as GraphQLError,
        ]) as readonly GraphQLError[],
  };
}

function wrapMutation<TInput>(
  mutate: (options: {
    variables: { input: TInput };
  }) => Promise<FetchResult<unknown>>
) {
  return async (input: TInput): Promise<FetchResult<unknown>> => {
    try {
      return await mutate({ variables: { input } });
    } catch (error) {
      return toFetchErrorResult(error as ApolloError);
    }
  };
}

export function useCreateAppVersionDraft(): [
  (input: CreateAppVersionDraftInput) => Promise<FetchResult<unknown>>,
  MutationState,
] {
  const [mutate, { loading, error }] = useAdminCreateAppVersionDraft({
    refetchQueries: summariesRefetch,
    awaitRefetchQueries: true,
    errorPolicy: 'all',
  });

  return [wrapMutation(mutate), { loading, error }];
}

export function useUpdateAppVersionDraft(): [
  (input: UpdateAppVersionDraftInput) => Promise<FetchResult<unknown>>,
  MutationState,
] {
  const [mutate, { loading, error }] = useAdminUpdateAppVersionDraft({
    refetchQueries: summariesRefetch,
    awaitRefetchQueries: true,
    errorPolicy: 'all',
  });

  return [wrapMutation(mutate), { loading, error }];
}

export function usePublishAppVersion(): [
  (
    input: PublishAppVersionInput,
    historyInput?: ListAppVersionsInput
  ) => Promise<FetchResult<unknown>>,
  MutationState,
] {
  const [mutate, { loading, error }] = useAdminPublishAppVersion({
    errorPolicy: 'all',
  });

  return [
    async (input, historyInput) => {
      const refetchQueries: InternalRefetchQueriesInclude = [
        ...summariesRefetch,
      ];
      if (historyInput) {
        refetchQueries.push({
          query: AdminListAppVersionsDocument,
          variables: { input: historyInput },
        });
      }

      try {
        return await mutate({
          variables: { input },
          refetchQueries,
          awaitRefetchQueries: true,
        });
      } catch (mutationError) {
        return toFetchErrorResult(mutationError as ApolloError);
      }
    },
    { loading, error },
  ];
}
